// Pure helpers for CSV upsert import (matching by id then normalized name).

export const normName = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();

const csvNum = (v) => parseFloat(String(v ?? "").trim().replace(/\s/g, "").replace(",", "."));

const catNameOf = (categories, id) => categories.find((c) => c.id === id)?.name || "—";

export const rowToBody = (r, categories, placeCatId) => {
  const cat =
    categories.find((c) => (c.name || "").toLowerCase() === (r.category || "").toLowerCase()) ||
    categories.find((c) => c.id === placeCatId) || categories[0];
  if (!cat) return null;
  return {
    name: (r.name || "Element").trim(),
    category_id: cat.id,
    length: csvNum(r.length) || cat.length,
    width: csvNum(r.width) || cat.width,
    consumption: csvNum(r.consumption) || 0,
    consumption_night: csvNum(r.consumption_night) || 0,
    x: csvNum(r.x) || 0,
    y: csvNum(r.y) || 0,
    rotation: csvNum(r.rotation) || 0,
    mobilier: (r.mobilier || "").trim(),
    branding: (r.branding || "").trim(),
    altele: (r.altele || "").trim(),
  };
};

const n = (v) => Number(v) || 0;

export const bodyDiffers = (body, el) => !(
  el.name === body.name &&
  el.category_id === body.category_id &&
  n(el.length) === n(body.length) &&
  n(el.width) === n(body.width) &&
  n(el.consumption) === n(body.consumption) &&
  n(el.consumption_night) === n(body.consumption_night) &&
  Math.round(n(el.x)) === Math.round(n(body.x)) &&
  Math.round(n(el.y)) === Math.round(n(body.y)) &&
  n(el.rotation) === n(body.rotation) &&
  (el.mobilier || "") === (body.mobilier || "") &&
  (el.branding || "") === (body.branding || "") &&
  (el.altele || "") === (body.altele || "")
);

const computeChanges = (body, el, categories) => {
  const ch = [];
  if (el.name !== body.name) ch.push(`nume: "${el.name}" → "${body.name}"`);
  if (el.category_id !== body.category_id) ch.push(`categorie: ${catNameOf(categories, el.category_id)} → ${catNameOf(categories, body.category_id)}`);
  if (n(el.length) !== n(body.length)) ch.push(`lungime: ${el.length} → ${body.length} m`);
  if (n(el.width) !== n(body.width)) ch.push(`lățime: ${el.width} → ${body.width} m`);
  if (n(el.consumption) !== n(body.consumption)) ch.push(`consum: ${el.consumption} → ${body.consumption} kW`);
  if (n(el.consumption_night) !== n(body.consumption_night)) ch.push(`consum noaptea: ${el.consumption_night || 0} → ${body.consumption_night} kW`);
  if (Math.round(n(el.x)) !== Math.round(n(body.x))) ch.push(`x: ${Math.round(n(el.x))} → ${Math.round(n(body.x))}`);
  if (Math.round(n(el.y)) !== Math.round(n(body.y))) ch.push(`y: ${Math.round(n(el.y))} → ${Math.round(n(body.y))}`);
  if (n(el.rotation) !== n(body.rotation)) ch.push(`rotație: ${el.rotation} → ${body.rotation}°`);
  ["mobilier", "branding", "altele"].forEach((f) => {
    if ((el[f] || "") !== (body[f] || "")) ch.push(`${f}: modificat`);
  });
  return ch;
};

// Turn CSV rows into an explicit action plan (create / update / skip).
export const buildImportPlan = (rows, elements, categories, placeCatId) => {
  const byId = new Map(), byName = new Map();
  elements.forEach((el) => { byId.set(el.id, el); byName.set(normName(el.name), el); });
  const seen = new Map();
  const plan = [];
  const addNames = [], updateNames = [];
  let unchanged = 0;
  for (const r of rows) {
    const body = rowToBody(r, categories, placeCatId);
    if (!body) continue;
    const rid = (r.id || "").trim();
    const key = normName(body.name);
    let targetEl = null, targetId = null;
    if (rid && byId.has(rid)) { targetEl = byId.get(rid); targetId = rid; }
    else if (seen.has(key)) { targetId = seen.get(key); }
    else if (byName.has(key)) { targetEl = byName.get(key); targetId = targetEl.id; }

    if (targetEl) {
      if (bodyDiffers(body, targetEl)) {
        plan.push({ action: "update", id: targetId, body });
        updateNames.push({ name: body.name, changes: computeChanges(body, targetEl, categories) });
      } else { plan.push({ action: "skip" }); unchanged++; }
      seen.set(key, targetId);
    } else if (targetId) {
      plan.push({ action: "update", key, body });
    } else {
      plan.push({ action: "create", key, body });
      addNames.push(body.name);
      seen.set(key, "__NEW__");
    }
  }
  return { plan, addNames, updateNames, unchanged };
};
